import React from 'react';

import { Link } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { useApi } from '../../../api/use-api';
import { jobDetailsPreviewRoute } from '../../../web-routes';

import { formatDateTimeShort } from '../../../util/formatters';
import { SORT_ORDER, sortByDate } from '../../../util/sort-functions';
import Message from '../../../widgets/legacy-message';
import { LoadingMessage } from '../../../widgets/loading-message';

type TableColumn = {
  key: string;
  label: string;
  component: (jobBoost: UnlinkedJobBoost) => JSX.Element;
};

const CRAIGSLIST_HOME_URL = 'https://accounts.craigslist.org/login/home';
const CRAIGSLIST_BOARD_SEARCH = (latitude: string | null, longitude: string | null) => {
  let craigslistBoardLink =
    'https://www.google.com/maps/d/u/1/viewer?mid=1mFVfmV0Wbh4qA10gfWvqC03xRNgnxf-F&z=10';
  if (latitude && longitude) {
    craigslistBoardLink += `&ll=${latitude}%2C${longitude}`;
  }
  return craigslistBoardLink;
};

const JobDetailsLink = (jobBoost: UnlinkedJobBoost) => (
  <span className="u-whiteSpace-nowrap">
    <Link className="typo-link" to={jobDetailsPreviewRoute(jobBoost.orgId, jobBoost.jobId)}>
      {jobBoost.orgName}
    </Link>
  </span>
);

const JobId = (jobBoost: UnlinkedJobBoost) => <div>{jobBoost.jobId}</div>;

const JobTitle = (jobBoost: UnlinkedJobBoost) => <div>{jobBoost.jobTitle}</div>;

const BoostStartDate = (jobBoost: UnlinkedJobBoost) => (
  <div>{formatDateTimeShort(jobBoost.startAt)}</div>
);

const ViewAds = () => (
  <span className="u-whiteSpace-nowrap">
    <a className="typo-link" href={CRAIGSLIST_HOME_URL} target="_blank" rel="noopener noreferrer">
      View Ads
    </a>
  </span>
);

const ViewMap = (jobBoost: UnlinkedJobBoost) => (
  <span className="u-whiteSpace-nowrap">
    <a
      className="typo-link"
      href={CRAIGSLIST_BOARD_SEARCH(jobBoost.latitude, jobBoost.longitude)}
      target="_blank"
      rel="noopener noreferrer"
    >
      View Map
    </a>
  </span>
);

const JobBoostRows = ({
  jobBoosts,
  tableColumns,
}: {
  jobBoosts: Array<UnlinkedJobBoost>;
  tableColumns: Array<TableColumn>;
}) => (
  <>
    {jobBoosts.map((jobBoost) => (
      <TableRow key={`unlinked-job-boost-${jobBoost.jobBoostId}`}>
        {tableColumns.map((col) => (
          <TableCell key={`col-${jobBoost.jobBoostId}-${col.key}`}>
            {col.component(jobBoost)}
          </TableCell>
        ))}
        {jobBoost.postingUrl ? (
          <TableCell>
            <span className="u-whiteSpace-nowrap">
              <a
                className="typo-link"
                href={jobBoost.postingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Create Ad
              </a>
            </span>
          </TableCell>
        ) : (
          <TableCell />
        )}
      </TableRow>
    ))}
  </>
);

const UnlinkedAdInstructions = () => (
  <div>
    <p>
      Listed below are jobs that have an existing Craigslist job boost, but have not been linked
      back to Team Engine. This could be for one of two reasons:
    </p>
    <ol>
      <li>
        The Craigslist Ad was created, but has not been linked yet. Click on the &quot;View
        Ads&quot; link and check if the ad has been created. If it has been, click the Craigslist
        &quot;Display&quot; link and use the browser extension to complete the link to Team Engine.
      </li>
      <li>
        The Craigslist Ad has not yet been created. You should first double check that a Help Scout
        request has been received for this job posting. You can click the &quot;View Map&quot; link
        to begin searching for the nearest Craigslist job board and creating the ad.
      </li>
    </ol>
  </div>
);

export const UnlinkedCraigslistAdBoosts = () => {
  const { data: unlinkedJobBoosts, errorMessage } = useApi<Array<UnlinkedJobBoost>>({
    url: '/craigslist/unlinked-job-boosts',
  });

  if (errorMessage) {
    return <Message variant="error">{errorMessage}</Message>;
  }

  if (!unlinkedJobBoosts) {
    return <LoadingMessage />;
  }

  const sortedJobBoosts = unlinkedJobBoosts.sort(
    sortByDate((jobBoost) => jobBoost.startAt, SORT_ORDER.ASC),
  );

  const tableColumns: Array<TableColumn> = [
    {
      key: 'org',
      label: 'Organization',
      component: JobDetailsLink,
    },
    {
      key: 'jobId',
      label: 'Job Id',
      component: JobId,
    },
    {
      key: 'jobTitle',
      label: 'Job Title',
      component: JobTitle,
    },
    {
      key: 'startAt',
      label: 'Started On',
      component: BoostStartDate,
    },
    {
      key: 'findAd',
      label: 'View Live Craigslist Ads',
      component: ViewAds,
    },
    {
      key: 'findJobBoard',
      label: 'Find Nearest Job Board',
      component: ViewMap,
    },
  ];

  return (
    <>
      <UnlinkedAdInstructions />
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              {tableColumns.map((c) => (
                <TableCell key={`col-head-${c.key}`}>{c.label}</TableCell>
              ))}
              <TableCell key="col-head-postingLink">Craigslist Board Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedJobBoosts.length ? (
              <JobBoostRows jobBoosts={sortedJobBoosts} tableColumns={tableColumns} />
            ) : (
              <TableRow>
                <TableCell colSpan={tableColumns.length}>
                  <div className="typo-title u-textAlign-center">
                    No unlinked Craigslist job boosts found
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};
